import type { FleetPeriod, Granularity } from "../types/chargingStationDashboard";

/**
 * BOF-76 — calendar helpers for the charging station dashboard.
 *
 * The server owns the definition of a period: it groups stored Berlin-local days into ISO weeks and
 * calendar months and returns each with its own `from`/`to`. These helpers only decide which *range of
 * days to ask for* and which period to open on. Dates are handled as local calendar dates (`YYYY-MM-DD`),
 * never as instants, so a timezone offset cannot move a day into its neighbour.
 */

/** How many periods the trend charts show, ending at the selected one. */
export const TREND_LENGTH: Record<Granularity, number> = {
  MONTH: 12,
  MONTH_ADJUSTED: 12,
  WEEK: 12,
};

const pad = (n: number) => String(n).padStart(2, "0");

/** `YYYY-MM-DD` for a local calendar date. */
export const toIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** A `YYYY-MM-DD` string as a local calendar date at midnight. */
export const parseIsoDate = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** The Monday of the ISO week containing `d`. */
export const startOfIsoWeek = (d: Date): Date => {
  const day = d.getDay(); // 0 = Sunday
  const offset = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + offset);
};

const isMonthly = (g: Granularity) => g === "MONTH" || g === "MONTH_ADJUSTED";

/** First and last day of the period of granularity `g` containing `anchor`. */
export const periodBounds = (g: Granularity, anchor: Date): { from: Date; to: Date } => {
  if (isMonthly(g)) {
    return {
      from: new Date(anchor.getFullYear(), anchor.getMonth(), 1),
      to: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0),
    };
  }
  const monday = startOfIsoWeek(anchor);
  return { from: monday, to: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6) };
};

/** The start of the period `n` periods away from the one containing `anchor` (negative = earlier). */
export const shiftPeriod = (g: Granularity, anchor: Date, n: number): Date => {
  const { from } = periodBounds(g, anchor);
  return isMonthly(g)
    ? new Date(from.getFullYear(), from.getMonth() + n, 1)
    : new Date(from.getFullYear(), from.getMonth(), from.getDate() + 7 * n);
};

/**
 * The day range covering the `count` periods that end with the one containing `anchor` — what to ask
 * the server for so the trend and the selected period come back in one request.
 */
export const trendBounds = (
  g: Granularity,
  anchor: Date,
  count: number = TREND_LENGTH[g],
): { from: string; to: string } => ({
  from: toIsoDate(periodBounds(g, shiftPeriod(g, anchor, -(count - 1))).from),
  to: toIsoDate(periodBounds(g, anchor).to),
});

/**
 * The start of the most recent *completed* period — what the dashboard opens on (FR-003). The running
 * period is incomplete by definition, and opening on it invites reading a part-month as a collapse.
 */
export const lastCompletedAnchor = (g: Granularity, today: Date = new Date()): Date =>
  shiftPeriod(g, today, -1);

/** True when `date` (`YYYY-MM-DD`) falls inside `period`. ISO strings compare chronologically. */
export const periodContains = (period: Pick<FleetPeriod, "from" | "to">, date: string): boolean =>
  period.from <= date && date <= period.to;

/** "22 of 30 days" — how much of a running period the figures cover. */
export const elapsedLabel = (period: Pick<FleetPeriod, "daysElapsed" | "daysInPeriod">): string =>
  `${period.daysElapsed} of ${period.daysInPeriod} days`;

export const GRANULARITY_LABEL: Record<Granularity, string> = {
  MONTH: "Monthly",
  WEEK: "Weekly",
  MONTH_ADJUSTED: "Monthly, 30-day adjusted",
};

/** What "previous" means to a reader, for change captions: "vs August 2026", "vs Week 37, 2026". */
export const comparisonLabel = (previous: Pick<FleetPeriod, "label"> | null | undefined): string =>
  previous ? `vs ${previous.label}` : "no previous period";

// ── Calendar months, for the KPI scorecard ───────────────────────────────────

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** `YYYY-MM` for the month containing `d`. */
export const monthKeyOf = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

/** "June 2026" for `2026-06`. */
export const monthLabel = (key: string): string => {
  const [y, m] = key.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

/** "Jun" for `2026-06` — for a chart axis whose year is already stated. */
export const shortMonthLabel = (key: string): string => MONTH_NAMES[Number(key.split("-")[1]) - 1].slice(0, 3);

/** `2025-12` for `2026-01`. */
export const previousMonthKey = (key: string): string => {
  const [y, m] = key.split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${pad(m - 1)}`;
};
